import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { faBooks, faHourglassHalf } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { RankmiTable } from '@rankmi/rankmi-table'
import { format } from 'date-fns'

import { faCheck } from '@fortawesome/pro-duotone-svg-icons'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import { coursesFastActionsFilterOptions } from 'enum/coursesFastActionsOptions.enum'
import { CourseStateType } from 'enum'
import { canSkipOnRowSelected } from 'helpers'
import { IMainState } from 'store/states'
import { generateTableLocale } from 'helpers/translations.helper'
import ConfirmationModal from 'components/ui/modals/confirmation/ConfirmationModal'
import CourseState from 'components/courses/CourseState'
import { ExternalCourseIndicator } from 'components/ui/CourseCards/components/molecules/externalCourseIndicator'
import DuplicateCourseModal from '../duplicateCourseModal/DuplicateCourseModal'
import Options from './options/Options'
import styles from './index.module.scss'

interface ICourseList {
  setPage: any
  setSearch: any
  setSort: any
  setPerPage: any
  setSelectedRows: any
  setFastFilters: any
  setAllSelected: any
  onDelete: any
}
const CourseList: React.FC<ICourseList> = ({
  setPage,
  setSearch,
  setSort,
  setPerPage,
  setSelectedRows,
  setFastFilters,
  setAllSelected,
  onDelete,
}) => {
  const { t } = useTranslation()
  const [dataSource, setDataSource] = useState<any>([])
  const [showModal, setShowModal] = useState<any>(0)
  const [translations, setTranslations] = useState<any>({})
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false)
  const { allProfilesList: lmsProfileList } = useSelector(
    ({ lmsProfiles }: IMainState) => lmsProfiles
  )
  const { list, total, loading } = useSelector(
    ({ courses }: IMainState) => courses
  )

  useEffect(() => {
    setTranslations(() => generateTableLocale())
  }, [])

  const [selected, setSelected] = useState({} as any)

  const prefix = 'administration.courses.list.create.table.headers'
  const columns = [
    {
      title: t(`${prefix}.name`),
      dataIndex: 'search_name',
      key: 'search_name',
      sorteable: true,
      width: 300,
      render: (name: string, data: any) => (
        <div className={styles.name}>
          <div>{name}</div>
          <ExternalCourseIndicator
            origin="TABLE"
            url={data['integrationCourseLink']}
          />
        </div>
      ),
    },
    {
      title: t(`${prefix}.forMandatoryProfiles`),
      dataIndex: 'mandatory',
      key: 'mandatory',
      variant: 'tag',
      type: 'tagsList',
      list: lmsProfileList
        .map(({ id, name, color }) => ({ value: id, label: name, color }))
        .sort((A, B) => A.label.localeCompare(B.label)),
    },
    {
      title: t(`${prefix}.forProfiles`),
      dataIndex: 'optional',
      key: 'optional',
      variant: 'tag',
      type: 'tagsList',
      list: lmsProfileList
        .map(({ id, name, color }) => ({ value: id, label: name, color }))
        .sort((A, B) => A.label.localeCompare(B.label)),
    },
    {
      title: t(`${prefix}.courseType.synchronous`),
      dataIndex: 'synchronous',
      key: 'synchronous',
      width: 85,
      render: (synchronous: boolean) =>
        synchronous ? (
          <FontAwesomeIcon
            icon={faCheck}
            style={{ color: '#36b37e', display: 'flex', margin: 'auto' }}
          />
        ) : null,
    },
    {
      title: t(`${prefix}.createAt`),
      sorteable: true,
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: any) => format(new Date(date), 'dd-MM-yyyy kk:mm'),
    },
    {
      title: t(`${prefix}.state`),
      sorteable: true,
      dataIndex: 'state',
      key: 'state',
      render: (state: CourseStateType) => <CourseState state={state} />,
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      width: 60,
      render: (
        id: any,
        {
          onDuplicated = false,
          name = '',
          courseType,
          integrationCourseLink,
          synchronous,
        }: any
      ) =>
        !onDuplicated ? (
          <Options
            id={id}
            name={name}
            courseType={courseType}
            onDeleteClick={(id: number, name: string) =>
              onDeleteClick(id, name)
            }
            onDuplicateClick={(id: number) => onDuplicateClick(id)}
            integrationCourseLink={integrationCourseLink}
            synchronous={synchronous}
          />
        ) : (
          <CommonToolTip
            text={t(
              'administration.courses.list.create.table.duplicateInProcess'
            )}
            position="left"
          >
            <FontAwesomeIcon
              icon={faHourglassHalf}
              className={styles.inProcess}
            />
          </CommonToolTip>
        ),
    },
  ]

  const fastAction = [
    {
      label: t(`commons.course.state.publishedCourses`),
      value: coursesFastActionsFilterOptions.publishedCourses,
    },
    {
      label: t(`commons.course.state.draftCourses`),
      value: coursesFastActionsFilterOptions.draftCourses,
    },
  ]

  useEffect(() => {
    setDataSource(
      list.map(
        ({
          id,
          name,
          state,
          requiredProfiles,
          optionalProfiles,
          createdAt,
          courseType,
          synchronous,
          integrationCourseLink,
        }) => ({
          key: id,
          search_name: name,
          mandatory: requiredProfiles,
          optional: optionalProfiles,
          created_at: createdAt,
          synchronous,
          state,
          courseType,
          action: id,
          integrationCourseLink,
        })
      )
    )
  }, [list])

  const onDeleteClick = (id: number, name: string) => {
    setShowDeleteConfirmationModal(true)
    setSelected({ id, name })
  }

  const onDuplicateClick = (id: number) => {
    setShowModal(id)
  }

  const handlePreDelete = ({ id, name }: any) => {
    onDelete(id)
    setShowDeleteConfirmationModal(false)
    setSelected({ id, name })
  }

  return (
    <div className={styles.container}>
      <RankmiTable
        columnHeaders={columns}
        data={dataSource}
        total={total}
        onPaginationChange={(page: any) => setPage(page)}
        onSearch={(text: string) => setSearch(text)}
        onSortChange={(sortData: any) => setSort(sortData)}
        loading={loading}
        fastActions={fastAction}
        onRowsPerPageChange={setPerPage}
        onRowSelected={({
          selectedRowKeys,
          allSelected,
          unselectedRowKeys,
          dispatcher,
        }: any) => {
          if (canSkipOnRowSelected(dispatcher)) return
          setSelectedRows(allSelected ? unselectedRowKeys : selectedRowKeys)
          setAllSelected(allSelected)
        }}
        locale={translations}
        countIcon={faBooks}
        initialRowsPerPage={50}
        simpleSelectAll
        onFastActionSelect={(selected: any) => setFastFilters(selected)}
      />
      {showDeleteConfirmationModal && (
        <ConfirmationModal
          show={showDeleteConfirmationModal}
          title=""
          text={t('administration.modal.courseDeleteConfirmation', {
            courseName: selected && selected.name.trim(),
          })}
          acceptButtonText={t('components.modal.buttons.delete')}
          onAccept={() => handlePreDelete(selected && selected)}
          onClose={() => setShowDeleteConfirmationModal(false)}
        />
      )}

      {!!showModal && (
        <DuplicateCourseModal
          show={showModal}
          onClose={() => setShowModal(0)}
        />
      )}
    </div>
  )
}

export default CourseList
